// 导入axios核心模块及相关类型定义
import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError, AxiosProgressEvent } from 'axios';
// 导入element-plus组件
import { ElMessage, ElLoading, ElProgress } from 'element-plus';
// 导入vue的虚拟节点工具
import { createVNode, render, VNode } from 'vue';
// 导入加载图标
import { Loading } from '@element-plus/icons-vue';
import router from '@/router';

/** 全局状态变量管理 */
let loadingInstance: ReturnType<typeof ElLoading.service> | null = null;
let requestCount = 0;
let currentPercentage = 0;

/** 创建请求进度条组件 */
const progressVNode: VNode = createVNode(ElProgress, {
  percentage: 0,
  strokeWidth: 2,
  style: {
    height: '3px',
    background: 'transparent',
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 9999
  }
});
render(progressVNode, document.body);

/** 将图标VNode转换为HTML字符串 */
const getSpinnerHtml = () => {
  const tempContainer = document.createElement('div');
  const iconVNode = createVNode(Loading, {
    size: 28,
    color: '#67c23a'
  });
  render(iconVNode, tempContainer);
  const html = tempContainer.innerHTML;
  render(null, tempContainer);
  return html;
};

/** 显示全屏加载状态 */
const showLoading = () => {
  requestCount++;
  
  if (requestCount === 1) {
    currentPercentage = 0;
    if (progressVNode.component) {
      progressVNode.component.props.percentage = 0;
    }
    loadingInstance = ElLoading.service({
      lock: true,
      text: '请稍等...',
      background: 'rgba(0, 0, 0, 0.6)',
      spinner: getSpinnerHtml()
    });
  }
};

/** 更新进度条百分比 */
const updateProgress = (percentage: number) => {
  currentPercentage = percentage;
  if (progressVNode.component) {
    progressVNode.component.props.percentage = percentage;
  }
};

/** 隐藏全屏加载状态 */
const hideLoading = () => {
  requestCount--;
  
  if (requestCount <= 0) {
    requestCount = 0;
    setTimeout(() => {
      if (loadingInstance) {
        loadingInstance.close();
        loadingInstance = null;
      }
      if (progressVNode.component) {
        progressVNode.component.props.percentage = 0;
      }
    }, 200);
  }
};

/** 创建axios实例 */
const service = axios.create({
  baseURL: 'https://xiaoyuancan.yundongkeji.cn',
  timeout: 120000,  // AI分析需要较长时间，增加超时时间到120秒
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
  },
  onUploadProgress: (progressEvent: AxiosProgressEvent) => {
    handleProgress(progressEvent);
  },
  onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
    handleProgress(progressEvent);
  }
});

/** 处理进度事件 */
const handleProgress = (progressEvent: AxiosProgressEvent) => {
  const { loaded, total } = progressEvent;
  
  if (total && loaded !== undefined) {
    const percentage = Math.round((loaded / total) * 100);
    if (percentage > currentPercentage) {
      updateProgress(percentage);
    }
  } else if (loaded && !total) {
    updateProgress(100);
  }
};

/** 请求拦截器 */
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 对文件导出请求特殊处理：不显示加载动画（可选）
    if (!config.headers?.['No-Loading'] && config.responseType !== 'blob') {
      showLoading();
    }
    
    let token = '';
    try {
      const userInfoStr = sessionStorage.getItem('userInfo');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        token = userInfo.token || '';
      }
    } catch (error) {
      console.error('解析token失败:', error);
    }
    
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error: AxiosError) => {
    hideLoading();
    ElMessage.error('请求发送失败');
    return Promise.reject(error);
  }
);

/** 响应拦截器 */
let lastErrorTime = 0;
const ERROR_THROTTLE_DELAY = 3000;
service.interceptors.response.use(
  (response: AxiosResponse) => {
    // 1. 处理文件流响应（导出接口专用）
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      hideLoading();
      // 检查是否为错误的blob（后端错误时可能返回json格式的blob）
      if (response.headers['content-type']?.includes('application/json')) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result as string);
              ElMessage.error(errorData.msg || '文件处理失败');
              reject(new Error(errorData.msg || '文件处理失败'));
            } catch (e) {
              reject(new Error('文件解析失败'));
            }
          };
          reader.readAsText(response.data);
        });
      }
      return response.data; // 直接返回blob数据
    }

    // 2. 处理普通JSON响应
    const { code, msg, data } = response.data;
    response.code = code;
    response.msg = msg;
    response.data = data;

    if (code !== 200) {
      ElMessage.error(msg || '操作失败');
      hideLoading();
      
      // 401登录过期处理
      if (code === 401) {
        ElMessage.error(msg || '登录过期，请重新登录');
        sessionStorage.clear();
        localStorage.clear();
        router.push('/login').catch(err => console.warn('路由跳转失败:', err));
      }
      return Promise.reject(new Error(msg || 'Error'));
    }
    
    hideLoading();
    return response;
  },
  (error) => {
    hideLoading();
    
    // 处理CORS和404错误
    const isCorsError = error.message.includes('CORS') || error.code === 'ERR_NETWORK';
    const is404Error = error.response?.status === 404;
    
    if (isCorsError || is404Error) {
      const now = Date.now();
      if (now - lastErrorTime > ERROR_THROTTLE_DELAY) {
        ElMessage({
          type: 'error',
          message: is404Error ? '请求的接口不存在' : '网络异常，跨域访问被阻止',
          onClose: () => {
            if (now - lastErrorTime > ERROR_THROTTLE_DELAY) {
              sessionStorage.clear();
              localStorage.clear();
              if (router.currentRoute.value.path !== '/login') {
                router.push('/login').catch(err => console.warn('路由跳转失败:', err));
              }
            }
          }
        });
        lastErrorTime = now;
      }
      return Promise.reject(error);
    }
    
    // 处理文件导出的错误响应
    if (error.response?.config.responseType === 'blob') {
      const errorBlob = error.response.data;
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result as string);
            ElMessage.error(errorData.msg || '导出失败');
            reject(new Error(errorData.msg || '导出失败'));
          } catch (e) {
            ElMessage.error('导出失败，请重试');
            reject(new Error('导出失败'));
          }
        };
        reader.readAsText(errorBlob);
      });
    }
    
    return Promise.reject(error);
  }
);

export default service;
    